import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule, DxLoadPanelModule, DxTreeListModule } from 'devextreme-angular';
import { RowDblClickEvent, RowPreparedEvent } from 'devextreme/ui/tree_list';
import { Subject, takeUntil } from 'rxjs';
import { DkFormPopupComponent, FormDto } from '@dabnet-kamu/libs/ui';
import { UstYaziPopupComponent } from '../ust-yazi-popup/ust-yazi-popup.component';
import { BelgeSilsileService, BysBelgeSilsileSatir } from './belge-silsile.service';

/**
 * DKM-5139 (Eyy-16): belge ilişki silsilesi — belge detayının (dk-pdf-view) "İlişkiler" sekmesi.
 * Köke çıkılıp tüm ağaç (önceki yazılar + cevaplar + aynı yazıya verilen diğer cevaplar) TreeList'te gösterilir.
 * Satırdan üst yazı açılır (dk-ust-yazi-popup; detay içinde ikinci detay açılmaz — PdfViewService kök singleton),
 * "Bu belgeye odaklan" ağacı o belgeden yeniden kurar. Yetki/gizlilik maskesi serviste uygulanır.
 */
@Component({
  selector: 'dk-belge-silsile',
  standalone: true,
  imports: [CommonModule, DxTreeListModule, DxButtonModule, DxLoadPanelModule, DkFormPopupComponent, UstYaziPopupComponent],
  templateUrl: './belge-silsile.component.html',
  styleUrls: ['./belge-silsile.component.scss'],
})
export class BelgeSilsileComponent implements OnChanges, OnDestroy {
  /** Detayı açık olan belge. */
  @Input() belgeKod: number;
  @Input() formMetaData: FormDto;

  /** Ağacın kurulduğu belge (odaklanınca değişir). */
  merkezKod: number;
  satirlar: BysBelgeSilsileSatir[] = [];
  /** autoExpandAll yalnız ilk yüklemede işler; her yüklemede tüm düğümler açık gelsin. */
  acikAnahtarlar: number[] = [];
  belgeSayisi = 0;
  kesildi = false;
  yukleniyor = false;
  hata: string = null;

  ustYaziVisible = false;
  ustYaziBelgeKod: number;
  /** DKM-5139 Cedric (Seher 12.10): taslak / imza iptal giden belgenin henüz e-imza PDF'i yok → Word içerik PDF önizleme olarak açılır. */
  ustYaziOnIzleme = false;

  private destroy$ = new Subject<void>();
  private istek$ = new Subject<void>();

  constructor(private service: BelgeSilsileService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['belgeKod'] && this.belgeKod) {
      this.yukle(this.belgeKod);
    }
  }

  ngOnDestroy(): void {
    this.istek$.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  yukle(kod: number) {
    if (!kod) return;
    this.istek$.next(); // önceki istek sürüyorsa bırak
    this.merkezKod = kod;
    this.yukleniyor = true;
    this.hata = null;
    this.service
      .getSilsile(kod)
      .pipe(takeUntil(this.istek$), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.yukleniyor = false;
          if (res?.result) {
            this.satirlar = res.satirlar ?? [];
            this.acikAnahtarlar = this.satirlar.map((s) => s.id);
            this.belgeSayisi = res.belgeSayisi ?? 0;
            this.kesildi = !!res.kesildi;
          } else {
            this.satirlar = [];
            this.belgeSayisi = 0;
            this.hata = res?.errorMessage || 'İlişki bilgisi alınamadı.';
          }
        },
        error: () => {
          // uç henüz yayında değilse (404) ya da ağ hatası: sekme boş durum gösterir, ekran bozulmaz
          this.yukleniyor = false;
          this.satirlar = [];
          this.belgeSayisi = 0;
          this.hata = 'İlişki bilgisi alınamadı.';
        },
      });
  }

  get iliskiYok(): boolean {
    return !this.yukleniyor && !this.hata && this.belgeSayisi <= 1 && !this.satirlar.some((s) => s.iliski === 'Serbest ilgi');
  }

  get merkezDegisti(): boolean {
    return !!this.merkezKod && this.merkezKod !== this.belgeKod;
  }

  seciliBelgeyeDon() {
    this.yukle(this.belgeKod);
  }

  yenile() {
    this.yukle(this.merkezKod || this.belgeKod);
  }

  ustYaziAc(satir: BysBelgeSilsileSatir) {
    if (!satir?.acilabilir || !satir.belgeKod) return;
    this.ustYaziBelgeKod = satir.belgeKod;
    // DKM-5139 Cedric: durum metni servisteki BysBelgeSilsile.DurumAdi'dan gelir (STATUSU 1=Taslak, 3=İmza İptal)
    this.ustYaziOnIzleme = satir.yon === 'Giden' && (satir.durum === 'Taslak' || satir.durum === 'İmza İptal');
    this.ustYaziVisible = true;
  }

  odaklan(satir: BysBelgeSilsileSatir) {
    if (!satir?.belgeKod || satir.belgeKod === this.merkezKod) return;
    this.yukle(satir.belgeKod);
  }

  onRowDblClick(e: RowDblClickEvent) {
    this.ustYaziAc(e?.data as BysBelgeSilsileSatir);
  }

  onRowPrepared(e: RowPreparedEvent) {
    if (e?.rowType !== 'data') return;
    const s = e.data as BysBelgeSilsileSatir;
    if (s.belgeKod && s.belgeKod === this.merkezKod) e.rowElement.classList.add('silsile-merkez');
    if (s.secili) e.rowElement.classList.add('silsile-secili');
    if (s.gizli) e.rowElement.classList.add('silsile-gizli');
    else if (!s.acilabilir) e.rowElement.classList.add('silsile-kapali');
  }

  // dx-tree-list buton görünürlükleri (arrow: this bağlamı korunur)
  ustYaziGorunur = (e: any) => !!e?.row?.data?.acilabilir && !!e?.row?.data?.belgeKod;
  odaklanGorunur = (e: any) => !!e?.row?.data?.belgeKod && e.row.data.belgeKod !== this.merkezKod;
  ustYaziTikla = (e: any) => this.ustYaziAc(e?.row?.data);
  odaklanTikla = (e: any) => this.odaklan(e?.row?.data);

  iliskiMetni = (s: BysBelgeSilsileSatir) =>
    s?.digerUstSayisi > 0 ? `${s.iliski} (+${s.digerUstSayisi} yazıya daha)` : s?.iliski;
}
